// eslint-disable-next-line react/no-danger
import { forwardRef, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import SoyuzAnalytics from '@spotahome/soyuz-analytics';

import { trans, useSoyuzExperiment } from '@spotahome/soyuz/client';

import { HomeCheckerShape } from '../../shapes/HomeChecker';

import VerificationModal from '../ListingLabel/VerificationModal';

import CheckedIconPhoto from '../ListingLabel/checkedInfo/ChekedIconPhoto';

import openLandlordVerifiedModal from '../LandlordVerifiedModal';
import openPropertyVerifiedModal from '../PropertyVerifiedModal';

import {
  InstantBookingFeature,
  SpotahomeGuaranteeFeature
} from './featureComponents';

import {
  IS_CHECKED_FEATURE,
  NO_DEPOSIT_FEATURE,
  INSTANT_BOOKING_FEATURE,
  GUARANTEE_FEATURE,
  IS_NOT_CHECKED_FEATURE,
  CHECK_24_HOURS_FEATURE,
  DIRECT_LINE_FEATURE,
  PROPERTY_READY_FEATURE,
  FEATURE_LIST,
  BILLS_INCLUDED_FEATURE,
  SOME_BILLS_INCLUDED_FEATURE,
  LANDLORD_TYPE_FEATURE,
  LONG_TERM_FEATURE,
  IS_BEST_CHOICE_FEATURE,
  IS_PLUS_FEATURE,
  DEPOSIT_PROTECTION_FEATURE,
  INSTANT_BOOKING_FEATURE_ELIGIBLE,
  GRACE_PERIOD_CANCELLATION_FEATURE
} from './featureList';
import Feature from './Feature';

import styles from './ValuePropositionFeatures.module.scss';
import LandlordType from './featureComponents/LandlordType';
import CheckedFeature from './featureComponents/CheckedFeature';
import GracePeriodCancellationFeature from './featureComponents/GracePeriodCancellationFeature';

const EMPTY_ARRAY = [];
const EMPTY_OBJECT = {};
const noop = () => {};

const ValuePropositionFeatures = ({
  vpRef = null,
  title = null,
  isChecked,
  showNotVerified = false,
  hasNoDeposit = false,
  hasDepositProtection = true,
  isInstantBookingEnabled,
  instantBookingRules = EMPTY_ARRAY,
  isInstantBookingElegible = false,
  isBillsIncluded,
  isSomeBillsIncluded,
  isLongTerm,
  isBestChoice,
  isPlus,
  landlordInformation = EMPTY_OBJECT,
  landlordScoring = null,
  onLandlordTypeClick = noop,
  homechecker = null,
  verificationDate = null,
  backgroundColor = 'white',
  className = null,
  isLighterBorder = false,
  dataTest = 'book-now-features'
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [collapsedItem, setCollapsableItemStatus] = useState('');

  const isGracePeriodMoreVisible =
    useSoyuzExperiment('GracePeriodMoreVisibility').get('behaviour') ===
    'variant';

  const isListingSimplificationHeaderVariant =
    useSoyuzExperiment('ListingSimplificationHeader').get('behaviour') ===
    'variant';

  const openVerificationModal = () => {
    SoyuzAnalytics.sendGA4Event('open', {
      section: 'more-about-verification'
    });
    setIsModalOpen(true);
  };

  const handlePropertyVerificationModalOpen = () => {
    if (isListingSimplificationHeaderVariant) {
      openPropertyVerifiedModal({ homechecker, verificationDate });
    } else {
      openVerificationModal();
    }
  };

  const handleLandlordVerificationModalOpen = () => {
    if (isListingSimplificationHeaderVariant) {
      openLandlordVerifiedModal({ landlordInformation, landlordScoring });
    } else {
      openVerificationModal();
    }
  };

  const showDepositProtection = !hasNoDeposit && hasDepositProtection;

  const handleOnCollapsedChange = (feature, isClosed) => {
    if (!isClosed) {
      SoyuzAnalytics.sendGA4Event('value-proposition-click', {
        section: feature
      });
    }

    setCollapsableItemStatus(feature);
  };

  const renderBookNowFeature = feature => {
    if (!FEATURE_LIST[feature]) {
      return null;
    }

    const { renderIcon, headerKey, isDangerState, renderBody } =
      FEATURE_LIST[feature];

    return (
      <Feature
        featureName={feature}
        collapsed={feature !== collapsedItem}
        onCollapsedChange={handleOnCollapsedChange}
        renderBody={renderBody}
        renderIcon={renderIcon}
        headerKey={headerKey}
        isDangerState={isDangerState}
      />
    );
  };

  const titleClasses = classNames(styles['booknow-card-features__title']);

  const alwaysWithSpotahomeClasses = classNames(
    titleClasses,
    styles['booknow-card-features__title--second']
  );

  return (
    <div
      ref={vpRef}
      className={classNames(
        styles['booknow-card-features'],
        styles[`booknow-card-features--${backgroundColor}-bg`],
        className,
        {
          [styles['booknow-card-features--lighter-border']]: isLighterBorder
        }
      )}
      data-test={dataTest}
    >
      <VerificationModal
        isOpen={isModalOpen}
        handleClose={() => setIsModalOpen(false)}
        checkedIcon={
          isChecked ? (
            <CheckedIconPhoto photoUrl={homechecker?.photoUrl} isVerified />
          ) : null
        }
      />
      {title && <h3 className={titleClasses}>{title}</h3>}
      <ul className={styles['booknow-card-features__list']}>
        {showDepositProtection &&
          renderBookNowFeature(DEPOSIT_PROTECTION_FEATURE)}
        {isChecked && (
          <CheckedFeature
            homechecker={homechecker}
            verificationDate={verificationDate}
            collapsed={![IS_CHECKED_FEATURE].includes(collapsedItem)}
            onCollapsedChange={handleOnCollapsedChange}
            openModal={handlePropertyVerificationModalOpen}
          />
        )}

        {hasNoDeposit && renderBookNowFeature(NO_DEPOSIT_FEATURE)}
        {isInstantBookingEnabled && !isInstantBookingElegible && (
          <InstantBookingFeature
            rules={instantBookingRules}
            onCollapsedChange={handleOnCollapsedChange}
            collapsed={![INSTANT_BOOKING_FEATURE].includes(collapsedItem)}
          />
        )}
        {isInstantBookingElegible &&
          renderBookNowFeature(INSTANT_BOOKING_FEATURE_ELIGIBLE)}
        {showNotVerified && renderBookNowFeature(IS_NOT_CHECKED_FEATURE)}
        {showNotVerified && renderBookNowFeature(PROPERTY_READY_FEATURE)}
        {showNotVerified && renderBookNowFeature(DIRECT_LINE_FEATURE)}
        {isBillsIncluded && renderBookNowFeature(BILLS_INCLUDED_FEATURE)}
        {isSomeBillsIncluded &&
          renderBookNowFeature(SOME_BILLS_INCLUDED_FEATURE)}
        {isBestChoice && renderBookNowFeature(IS_BEST_CHOICE_FEATURE)}
        {isPlus && renderBookNowFeature(IS_PLUS_FEATURE)}
        {isLongTerm && renderBookNowFeature(LONG_TERM_FEATURE)}
        {landlordInformation && (
          <LandlordType
            landlordInformation={landlordInformation}
            onCollapsedChange={handleOnCollapsedChange}
            collapsed={![LANDLORD_TYPE_FEATURE].includes(collapsedItem)}
            openVerificationModal={handleLandlordVerificationModalOpen}
            loading={false}
            onClick={onLandlordTypeClick}
          />
        )}
      </ul>

      <h3 className={alwaysWithSpotahomeClasses}>
        {trans('value.proposition.features.title.always')}
      </h3>
      <ul className={styles['booknow-card-features__list']}>
        {isGracePeriodMoreVisible && (
          <GracePeriodCancellationFeature
            onCollapsedChange={handleOnCollapsedChange}
            collapsed={
              ![GRACE_PERIOD_CANCELLATION_FEATURE].includes(collapsedItem)
            }
          />
        )}
        <SpotahomeGuaranteeFeature
          onCollapsedChange={handleOnCollapsedChange}
          collapsed={![GUARANTEE_FEATURE].includes(collapsedItem)}
        />
        {renderBookNowFeature(CHECK_24_HOURS_FEATURE)}
      </ul>
    </div>
  );
};

ValuePropositionFeatures.propTypes = {
  title: PropTypes.string,
  isChecked: PropTypes.bool.isRequired,
  showNotVerified: PropTypes.bool,
  hasNoDeposit: PropTypes.bool.isRequired,
  hasDepositProtection: PropTypes.bool,
  isInstantBookingEnabled: PropTypes.bool.isRequired,
  instantBookingRules: PropTypes.arrayOf(
    PropTypes.shape({
      type: PropTypes.string,
      params: PropTypes.shape({
        [PropTypes.string]: PropTypes.oneOfType([
          PropTypes.string,
          PropTypes.number
        ])
      })
    })
  ),
  isBillsIncluded: PropTypes.bool.isRequired,
  isSomeBillsIncluded: PropTypes.bool.isRequired,
  isLongTerm: PropTypes.bool.isRequired,
  isPlus: PropTypes.bool.isRequired,
  isBestChoice: PropTypes.bool.isRequired,
  landlordInformation: PropTypes.shape({
    type: PropTypes.string
  }),
  landlordScoring: PropTypes.shape({
    score: PropTypes.number,
    totalBookingsClosed: PropTypes.number
  }),
  onLandlordTypeClick: PropTypes.func,
  homechecker: PropTypes.shape(HomeCheckerShape),
  verificationDate: PropTypes.string,
  backgroundColor: PropTypes.oneOf(['white', 'blue']),
  className: PropTypes.string,
  isLighterBorder: PropTypes.bool,
  vpRef: PropTypes.oneOfType([PropTypes.func, PropTypes.shape({})]),
  isInstantBookingElegible: PropTypes.bool,
  dataTest: PropTypes.string
};

export default forwardRef((props, ref) => (
  <ValuePropositionFeatures {...props} vpRef={ref} />
));
